import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Clock, Users, MousePointerClick, CheckCircle2, MessageSquare, EyeOff, Loader2, MapPin } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge/Badge';
import {
  proposalService,
  type ProposalAnalytics,
} from '@/services/ProposalService';
import { formatVisitLocation } from './visitLocation';

interface ProposalAnalyticsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  linkToken?: string | null;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Human names for the internal section ids the beacon reports. */
const SECTION_LABELS: Record<string, string> = {
  overview: 'Overview',
  agenda: 'Agenda',
  compare: 'Comparison',
  budget: 'Budget',
  'sign-off': 'Sign-off',
  signoff: 'Sign-off',
  transportation: 'Transportation',
};

/** How many locations the summary lists before collapsing into "+N more". */
const TOP_LOCATIONS_LIMIT = 5;

function visitEngagement(v: ProposalAnalytics['visits'][number]): string {
  const parts: string[] = [];
  if (v.sections.length > 0) parts.push(`${v.sections.length} section${v.sections.length > 1 ? 's' : ''}`);
  if (v.venue_ids.length > 0) parts.push(`${v.venue_ids.length} hotel${v.venue_ids.length > 1 ? 's' : ''}`);
  return parts.join(' · ');
}

function sectionLabel(id: string): string {
  const known = SECTION_LABELS[id];
  if (known) return known;
  // Destination sections report their internal id (often a uuid) — keep it readable
  if (/^[0-9a-f]{8}-[0-9a-f]{4}|^[0-9a-f]{16,}/i.test(id)) return 'Destination';
  return id.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ProposalAnalyticsDrawer({ isOpen, onClose, eventId, linkToken }: ProposalAnalyticsDrawerProps) {
  const [data, setData] = useState<ProposalAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const analytics = await proposalService.getProposalAnalytics(eventId, 'proposal');
      setData(analytics);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isOpen, eventId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const link = data?.links?.find((l) => l.token === linkToken) ?? data?.links?.[0] ?? null;
  const opened = !!link?.first_viewed_at;
  const shortlistedVenueIds = new Set((data?.shortlist ?? []).map((s) => s.venue_id));
  const venueNameById = new Map((data?.venueNames ?? []).map((v) => [v.id, v.name]));
  const locations = data?.locations ?? [];
  const topLocations = locations.slice(0, TOP_LOCATIONS_LIMIT);
  const maxLocationVisits = topLocations[0]?.visit_count || 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Proposal Link Analytics"
      description="Who opened the proposal, when, and what caught their eye"
      size="xl"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
        </div>
      ) : !data || !link ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <EyeOff className="w-8 h-8 mx-auto mb-3 opacity-40" />
          No proposal link has been created yet.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Status */}
          <div
            className={`rounded-lg p-4 border ${
              opened ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}
          >
            {opened ? (
              <div className="flex items-center gap-2 text-sm">
                <Eye className="w-4 h-4 text-green-600" />
                <span className="font-medium">
                  Opened {data.totals.client_visit_count > 1 ? `${data.totals.client_visit_count}×` : ''} — first viewed{' '}
                  {formatDate(link.first_viewed_at)}
                </span>
                {data.feedback.length > 0 && (
                  <Badge variant="success" className="ml-auto">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    {data.feedback.length} feedback{data.feedback.length > 1 ? 's' : ''}
                  </Badge>
                )}
                {data.shortlist.length > 0 && (
                  <Badge variant="success">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {data.shortlist.length} shortlisted
                  </Badge>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <EyeOff className="w-4 h-4" />
                <span>Not opened yet — the badge here updates the moment your client opens the link.</span>
              </div>
            )}
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Eye className="w-3.5 h-3.5" /> Opens
              </div>
              <div className="text-2xl font-bold mt-1">{link.view_count ?? 0}</div>
              <div className="text-[11px] text-muted-foreground">client visits · refresh-proof</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Clock className="w-3.5 h-3.5" /> Read time
              </div>
              <div className="text-2xl font-bold mt-1">{formatDuration(data.totals.dwell_seconds)}</div>
              <div className="text-[11px] text-muted-foreground">total across visits</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <MousePointerClick className="w-3.5 h-3.5" /> Hotels viewed
              </div>
              <div className="text-2xl font-bold mt-1">{data.hotels.length}</div>
              <div className="text-[11px] text-muted-foreground">opened the details</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Users className="w-3.5 h-3.5" /> Visitors
              </div>
              <div className="text-2xl font-bold mt-1">{data.totals.unique_viewers || data.totals.client_visit_count}</div>
              <div className="text-[11px] text-muted-foreground">
                {data.emails.length > 0 ? `${data.emails.length} identified` : 'anonymous'}
              </div>
            </div>
          </div>

          {/* Where client visits came from (approximate, from IP at visit time) */}
          {topLocations.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-muted-foreground" /> Top locations
              </h4>
              <div className="space-y-1.5">
                {topLocations.map((l) => {
                  const label = formatVisitLocation(l);
                  return (
                    <div key={`${l.country_code}-${l.region_name ?? ''}`} className="flex items-center gap-2 text-sm">
                      <div className="w-44 sm:w-56 shrink-0 truncate" title={label ?? undefined}>{label}</div>
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent-500"
                          style={{ width: `${Math.max(12, (l.visit_count / maxLocationVisits) * 100)}%` }}
                        />
                      </div>
                      <div className="w-14 text-right text-xs text-muted-foreground">
                        {l.visit_count} visit{l.visit_count > 1 ? 's' : ''}
                      </div>
                    </div>
                  );
                })}
                {locations.length > TOP_LOCATIONS_LIMIT && (
                  <div className="text-xs text-muted-foreground">
                    +{locations.length - TOP_LOCATIONS_LIMIT} more location{locations.length - TOP_LOCATIONS_LIMIT > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Visit timeline */}
          {data.visits.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Visits</h4>
              <div className="rounded-lg border divide-y max-h-64 overflow-y-auto">
                {data.visits.map((v) => {
                  const location = formatVisitLocation(v);
                  const engagement = visitEngagement(v);
                  return (
                    <div key={`${v.link_id}-${v.session_key}`} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                      <div className="w-28 sm:w-32 shrink-0">
                        <div className="font-medium">{formatDate(v.first_seen)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {v.last_seen !== v.first_seen ? `until ${formatDate(v.last_seen)}` : ''}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        {v.actor_type === 'platform' ? (
                          <span className="text-muted-foreground italic">
                            Team preview{v.email ? ` · ${v.email}` : ''} <span className="text-[10px]">(not counted)</span>
                          </span>
                        ) : v.email ? (
                          <span className="truncate">{v.email}</span>
                        ) : (
                          <span className="text-muted-foreground">Anonymous visitor</span>
                        )}
                        {v.actor_type !== 'platform' && (
                          <div
                            className="flex items-center gap-1 text-[11px] text-muted-foreground"
                            title={location ? 'Approximate location at the time of the visit' : 'Location not recorded (visit before location tracking)'}
                          >
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{location ?? '—'}</span>
                          </div>
                        )}
                        {/* Phones: engagement folds under the visitor instead of fixed columns */}
                        <div className="sm:hidden text-[11px] text-muted-foreground">
                          {[formatDuration(v.dwell_seconds), engagement].filter((x) => x && x !== '—').join(' · ')}
                        </div>
                      </div>
                      <div className="hidden sm:block w-16 text-right text-xs text-muted-foreground">{formatDuration(v.dwell_seconds)}</div>
                      <div className="hidden sm:block w-28 text-right text-xs text-muted-foreground">{engagement}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* What they looked at */}
          {(data.sections.length > 0 || data.hotels.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {data.sections.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Sections viewed</h4>
                  <div className="space-y-1.5">
                    {data.sections.map((s) => {
                      const max = data.sections[0]?.visit_count || 1;
                      return (
                        <div key={s.section} className="flex items-center gap-2 text-sm">
                          <div className="w-28 shrink-0 truncate">{sectionLabel(s.section)}</div>
                          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-accent-500"
                              style={{ width: `${Math.max(12, (s.visit_count / max) * 100)}%` }}
                            />
                          </div>
                          <div className="w-8 text-right text-xs text-muted-foreground">{s.visit_count}×</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {data.hotels.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Hotels they explored</h4>
                  <div className="space-y-1.5">
                    {data.hotels.map((h) => (
                      <div key={h.venue_id} className="flex items-center gap-2 text-sm">
                        <div className="flex-1 truncate">{venueNameById.get(h.venue_id) ?? 'Hotel'}</div>
                        {shortlistedVenueIds.has(h.venue_id) && (
                          <Badge variant="success" className="text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            shortlisted
                          </Badge>
                        )}
                        <div className="w-8 text-right text-xs text-muted-foreground">{h.visit_count}×</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Feedback */}
          {data.feedback.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Client feedback</h4>
              <div className="space-y-2">
                {data.feedback.map((f, i) => (
                  <div key={i} className="rounded-lg border p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{f.name}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(f.created_at)}</span>
                    </div>
                    <p className="text-muted-foreground whitespace-pre-wrap">{f.comments}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground">
            Public-link visits are anonymous — no cookies, no IP addresses stored. Location is approximate
            (country; state or province for the US and Canada), worked out at the time of the visit — the IP
            itself is discarded. Emails appear only for access-code (gated) links. Your own previews are
            recorded but never counted.{' '}
            <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              IP Geolocation by DB-IP
            </a>
          </p>
        </div>
      )}
    </Modal>
  );
}
